'use client';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import useUploadPhoto from '@/hooks/useUploadPhoto';
import { Image as ImageIcon, Upload, User } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { toast } from 'sonner';

type Props = {
  profileImage?: string | null;
  onSuccess: (data: { url: string }) => void;
};

export default function AvatarUploader({ profileImage, onSuccess }: Props) {
  const [open, setOpen] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const { mutate: changePhoto, isPending: uploadPending } = useUploadPhoto();

  useEffect(() => {
    return () => {
      if (selectedUrl) URL.revokeObjectURL(selectedUrl);
    };
  }, [selectedUrl]);

  const handleChangePhoto = (file: File) => {
    if (!file) {
      toast.error('No file selected');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    changePhoto(formData, {
      onSuccess: (successData) => {
        toast.success('Profile photo updated successfully');
        onSuccess(successData);
        handleCancel();
      },
      onError: (error) => {
        toast.error(`Failed to upload image: ${error.message}`);
      },
    });
  };

  const handleImageChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setError(null);
    const file = e.target.files?.[0];
    if (!file) return;

    const isOkType = /^image\/(png|jpe?g|webp)$/i.test(file.type);
    if (!isOkType) {
      setError('Please select a PNG, JPG, or WEBP image.');
      e.currentTarget.value = '';
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError('File is larger than 20MB.');
      e.currentTarget.value = '';
      return;
    }

    const url = URL.createObjectURL(file);
    setSelectedFile(file);
    setSelectedUrl(url);
    setZoom(1);
    setRotation(0);
    setCrop({ x: 0, y: 0 });
    setOpen(true);
  };

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedPixels: Area) => {
      setCroppedAreaPixels(croppedPixels);
    },
    [],
  );

  const handleCancel = () => {
    // reset only the cropper state, keep current profileImage
    if (selectedUrl) URL.revokeObjectURL(selectedUrl);
    setSelectedUrl(null);
    setSelectedFile(null);
    setOpen(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!selectedUrl || !croppedAreaPixels) return;

    try {
      const blob = await getCroppedBlob(
        selectedUrl,
        croppedAreaPixels,
        rotation,
      );
      const file = new File(
        [blob],
        makeFileName(selectedFile?.name ?? 'avatar.png'),
        { type: blob.type },
      );

      handleChangePhoto(file);
    } catch (err) {
      console.error(err);
      setError('Failed to crop image. Please try another photo.');
    }
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      <Avatar className="w-32 h-32 border-2 border-tax-borderGray">
        <AvatarImage src={profileImage || '/placeholder.svg'} />
        <AvatarFallback className="bg-tax-actionOrange text-white text-2xl">
          <User />
        </AvatarFallback>
      </Avatar>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="flex gap-2">
            <ImageIcon className="h-4 w-4" />
            Edit Photo
          </Button>
        </DialogTrigger>

        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Profile Picture</DialogTitle>
            <DialogDescription>
              Upload a professional photo for your profile. You can crop and
              resize after selecting.
            </DialogDescription>
          </DialogHeader>

          {/* Preview (current) */}
          {profileImage && (
            <div className="flex justify-center">
              <div className="relative w-32 h-32 mb-2">
                <img
                  src={profileImage}
                  alt="Current profile"
                  className="rounded-full object-cover w-full h-full border-2 border-tax-borderGray"
                />
              </div>
            </div>
          )}

          {/* Drop area / picker */}
          {!selectedUrl && (
            <div className="flex items-center justify-center w-full">
              <label
                htmlFor="profile-upload"
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-tax-borderGray border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 mb-2 text-gray-500" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> or
                    drag and drop
                  </p>
                  <p className="text-xs text-gray-500">
                    PNG, JPG or WEBP (MAX. 20MB)
                  </p>
                </div>
                <input
                  id="profile-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>
            </div>
          )}

          {/* Cropper */}
          {selectedUrl && (
            <div className="space-y-4">
              <div className="relative w-full h-64 rounded-md overflow-hidden bg-black/5">
                <Cropper
                  image={selectedUrl}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={1} // square crop for circular avatar
                  cropShape="round" // shows a circular mask
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onRotationChange={setRotation}
                  onCropComplete={onCropComplete}
                />
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="flex items-center gap-3">
                  <label className="w-16 text-sm text-gray-600">Zoom</label>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.01}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="w-full"
                    aria-label="Zoom"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <label className="w-16 text-sm text-gray-600">Rotate</label>
                  <input
                    type="range"
                    min={-180}
                    max={180}
                    step={1}
                    value={rotation}
                    onChange={(e) => setRotation(Number(e.target.value))}
                    className="w-full"
                    aria-label="Rotation"
                  />
                </div>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <DialogFooter className="flex justify-between">
            <Button variant="outline" type="button" onClick={handleCancel}>
              Cancel
            </Button>
            <div className="flex gap-2">
              {!selectedUrl && (
                <label htmlFor="profile-upload">
                  <Button asChild variant="secondary" type="button">
                    <span>Choose Image</span>
                  </Button>
                </label>
              )}
              {selectedUrl && (
                <Button
                  disabled={uploadPending}
                  type="button"
                  onClick={handleSave}
                >
                  Save Changes
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ----------------------- helpers ----------------------- */

function makeFileName(original: string) {
  const base = original.replace(/\.[^/.]+$/, '');
  return `${base || 'avatar'}_cropped.png`;
}

function createImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (e) => reject(e));
    // Needed to avoid cross-origin issues with object URLs / local files
    img.crossOrigin = 'anonymous';
    img.src = src;
  });
}

// Adapted for rotation support. Returns a PNG Blob of the cropped area.
async function getCroppedBlob(
  imageSrc: string,
  pixelCrop: Area,
  rotation = 0,
): Promise<Blob> {
  const image = await createImage(imageSrc);

  const radians = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(radians));
  const cos = Math.abs(Math.cos(radians));
  const bBoxWidth = image.width * cos + image.height * sin;
  const bBoxHeight = image.width * sin + image.height * cos;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2D context');

  // draw the rotated image onto a temp canvas first
  const tempCanvas = document.createElement('canvas');
  const tctx = tempCanvas.getContext('2d');
  if (!tctx) throw new Error('No 2D context');
  tempCanvas.width = bBoxWidth;
  tempCanvas.height = bBoxHeight;

  tctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  tctx.rotate(radians);
  tctx.drawImage(image, -image.width / 2, -image.height / 2);

  // now crop the desired pixels from the rotated canvas
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Optional circular mask so the output is already round (PNG with transparency)
  ctx.save();
  const radius = Math.min(canvas.width, canvas.height) / 2;
  ctx.beginPath();
  ctx.arc(canvas.width / 2, canvas.height / 2, radius, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();

  ctx.drawImage(
    tempCanvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );
  ctx.restore();

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error('Canvas is empty'));
      resolve(blob);
    }, 'image/png');
  });
}
